import { Component, inject, input, output, signal, TemplateRef, viewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { FileInputType } from "../../../../shared/constants/enums";
import { Constants } from "../../../constants/constants";
import { Language, PageMode } from "../../../constants/enums";
import { AttachFile, ValidatorModelType } from "../../../models";
import { FaqCategory } from "../../../models/online-help-model";
import { AppService, ValidatorApiService } from "../../../services";
import { FormActionButtonsComponent, FormInputComponent, FormInputFileComponent } from "../../form";

@Component({
  selector: "adaa-faqs-category-editor",
  imports: [FormInputComponent, FormActionButtonsComponent, TranslatePipe, ReactiveFormsModule, FormInputFileComponent],
  templateUrl: "./faqs-category-editor.html",
  styleUrl: "./faqs-category-editor.scss",
})
export class FaqsCategoryEditorComponent {
  private _formBuilder = inject(FormBuilder);
  private _modalService = inject(NgbModal);
  private _appService = inject(AppService);
  private _translateService = inject(TranslateService);
  private _toastrService = inject(ToastrService);
  private _validatorApiService = inject(ValidatorApiService);

  formValidation = signal<ValidatorModelType | undefined>(undefined);
  faqsCategoryForm: FormGroup = new FormGroup({});
  submitted = signal<boolean>(false);
  modal = viewChild.required<TemplateRef<unknown>>("content");
  pageMode = input<string>();
  category = output<FaqCategory>();
  modalRef: NgbModalRef;
  PageMode = PageMode;
  FileInputType = FileInputType;

  public ngOnInit(): void {
    this._prepareForm();
  }

  private _prepareForm() {
    const isViewMode = this.pageMode() === PageMode.view;

    this.faqsCategoryForm = this._formBuilder.group({
      nameAE: { value: null, disabled: isViewMode },
      nameEN: { value: null, disabled: isViewMode },
      iconAttachment: { value: null, disabled: isViewMode },
    });

    this._validatorApiService
      .applyValidationToForm(Constants.VALIDATORS_CONF_KEY.VALIDATION_CATEGORY_FAQS, this.faqsCategoryForm)
      .subscribe({
        next: (response) => {
          this.formValidation.set(response.validation);
          this.faqsCategoryForm = response.form;
        },
      });
  }

  public open() {
    const modalRef = this._modalService.open(this.modal(), {
      animation: true,
      scrollable: false,
      keyboard: false,
      size: "lg",
      centered: true,
      modalDialogClass: this._appService.language() === Language.Arabic ? "modal-rtl" : "modal-ltr",
    });

    this.modalRef = modalRef;
    modalRef.dismissed.subscribe(() => this._resetForm());
    modalRef.closed.subscribe(() => this._resetForm());
  }

  public submit() {
    this.submitted.set(true);
    if (this.faqsCategoryForm.invalid) {
      this._toastrService.error(this._translateService.instant("notification.warning.missing_info"));
      return;
    }

    this.category.emit(this.faqsCategoryForm.value);
    this.modalRef.close();
  }

  public checkInvalid(control: string) {
    const result =
      this.faqsCategoryForm.controls[control]?.errors?.["required"] &&
      (this.faqsCategoryForm.get(control)?.dirty || this.submitted());
    return result;
  }

  private _resetForm() {
    this.faqsCategoryForm.reset();
    this.faqsCategoryForm.markAsPristine();
    this.faqsCategoryForm.markAsUntouched();
    this.submitted.set(false);
  }

  public getImage() {
    const attach = this.faqsCategoryForm.get("iconAttachment")?.value;
    return {
      id: attach?.id ?? 0,
      filename: attach?.filename ?? "",
    };
  }

  public onFileChange(event: AttachFile | null) {
    if (event) {
      this.faqsCategoryForm.get("iconAttachment")?.setValue({
        id: event.id,
        attachFile: null,
        filename: event.filename,
        activityId: null,
        taskId: null,
        auxAttachFile: {
          filename: null,
        },
      });
    } else this.faqsCategoryForm.get("iconAttachment")?.setValue(null);
  }
}
